"use client";

/**
 * Client half of the root layout.
 *
 * Replaces the old pages-router Layout.tsx. All layout DATA (menus, nav,
 * social links, page settings, welcome popup, tawk settings) now arrives
 * as props from the server layout (cached 5 min) — this component only
 * keeps the interactive behaviour: auth/cart sync, GTM dataLayer, welcome
 * popup cookies, tawk widget handling.
 */

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import Cookies from "js-cookie";
// react-toastify v11+ injects its styles automatically (no CSS import needed)
import { ToastContainer } from "react-toastify";

import Footer from "./Footer";
import Header from "./Header";

import BackToTop from "@/components/BackToTop";
import WelcomePopup, {
  checkClosedTime,
  getWelcomeHashKey,
} from "@/components/WelcomePopup";
import { logout, validateTokenAsync } from "@/store/features/authSlice";
import {
  addToCartAsync,
  clearCart,
  getCartListAsync,
  setDraftRequest,
} from "@/store/features/cartSlice";
import {
  setPageSettings,
  setSocialLinksWidget,
} from "@/store/features/globalSlice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  getLocalStorageData,
  getSectionStyle,
  getTokenCookie,
  randomUserId,
  setLocalStorageData,
} from "@/utils";
import { envs } from "@/utils/constants";
import type { LayoutData } from "@/lib/layout-data";

interface Props extends LayoutData {
  children: React.ReactNode;
}

const LayoutShell = ({
  children,
  menuItems,
  navItems,
  socialLinks,
  pageSettingsWidget,
  welcomePopup,
}: Props) => {
  const dispatch = useAppDispatch();
  const pathname = usePathname();
  const isLoggedIn = useAppSelector((state) => !!state.auth.access_token);
  const user = useAppSelector((state) => state.auth.user);
  const cartDraftRequest = useAppSelector((state) => state.cart.draftRequest);
  const globalPopupElement = useAppSelector(
    (state) => state.global.globalPopupElement,
  );
  const builderPageSettings = useAppSelector(
    (state) => state.global.builderPageSettings,
  );

  // console.log("welcomePopup", welcomePopup);

  const [welcomeImage, setWelcomeImage] = useState<{
    desktop: PopupWidgetImage;
    mobile: PopupWidgetImage;
    resetTime?: number;
    numberOfTimes?: number;
  }>();

  // hydrate global store from server-fetched widgets
  useEffect(() => {
    if (socialLinks) dispatch(setSocialLinksWidget(socialLinks));
    if (pageSettingsWidget) dispatch(setPageSettings(pageSettingsWidget));
  }, [dispatch, socialLinks, pageSettingsWidget]);

  // auth token validation
  useEffect(() => {
    const token = getTokenCookie();
    if (token) {
      dispatch(validateTokenAsync(token));
    } else {
      dispatch(logout());
    }
  }, [dispatch]);

  // welcome popup (data comes from the server; cookie logic stays client-side)
  useEffect(() => {
    if (!welcomePopup) return;

    const hashLocal = Cookies.get(envs.welcomePopupHashKey);
    const timer = setTimeout(() => {
      const data = welcomePopup;
      const settings = data.settings || data;
      const resolveImage = (banner: any): PopupWidgetImage | undefined => {
        const image = banner?.image ?? banner;
        if (typeof image === "string") {
          return image ? { url: image } : undefined;
        }
        return image?.url ? image : undefined;
      };
      const desktopImage =
        resolveImage(settings.banner?.desktopImage) ||
        resolveImage(settings.banner) ||
        resolveImage(data.banner?.desktopImage) ||
        resolveImage(data.banner);
      const mobileImage =
        resolveImage(settings.banner?.mobileImage) ||
        resolveImage(settings.banner2) ||
        resolveImage(data.banner?.mobileImage) ||
        resolveImage(data.banner2);
      const popupIsActive = (settings.active ?? data.active) !== false;
      const resetTimeValue = Number(settings.resetTime);
      const numberOfTimesValue = Number(settings.numberOfTimes);
      const resetTime =
        Number.isFinite(resetTimeValue) && resetTimeValue > 0
          ? resetTimeValue
          : undefined;
      const numberOfTimes =
        Number.isFinite(numberOfTimesValue) && numberOfTimesValue > 0
          ? numberOfTimesValue
          : undefined;
      const hashApi = getWelcomeHashKey(desktopImage || mobileImage, resetTime);

      if (
        popupIsActive &&
        (desktopImage?.url || mobileImage?.url) &&
        (Cookies.get(envs.welcomePupupCloseKey) !== "1" ||
          (hashLocal && hashLocal !== hashApi))
      ) {
        const closedCount = Number(
          Cookies.get(envs.welcomePopupCountKey) || "0",
        );
        const hasDisplayLimit = numberOfTimes !== undefined;

        if (
          (!hasDisplayLimit || closedCount < numberOfTimes)
        ) {
          setWelcomeImage({
            desktop: desktopImage || mobileImage!,
            mobile: mobileImage || desktopImage!,
            resetTime,
            numberOfTimes,
          });

          Cookies.set(envs.welcomePopupHashKey, hashApi, { expires: 365 });
          Cookies.remove(envs.welcomePupupCloseKey);
        }
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [welcomePopup]);

  // tawk chat widget: reposition iframe + hide on checkout
  useEffect(() => {
    let step = 0;
    const interval = setInterval(() => {
      const iframe = document.querySelector(
        "iframe[title='chat widget']",
      ) as HTMLIFrameElement;

      if (iframe) {
        iframe.style.bottom = "54px";
        iframe.style.right = "0px";
        clearInterval(interval);
      }

      if (step > 10) clearInterval(interval);
      step++;
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const tawk = (window as any).Tawk_API;
    const updateTawkVisibility = () => {
      const shouldHide = pathname === "/checkout";

      const targetFrames = document.querySelectorAll(
        'iframe[title="chat widget"], iframe[id*="tawk"], iframe[src*="tawk.to"]',
      );

      targetFrames.forEach((frame) => {
        const element = frame as HTMLElement;
        element.style.display = shouldHide ? "none" : "block";
        element.style.visibility = shouldHide ? "hidden" : "visible";
      });

      if (shouldHide) {
        tawk?.hideWidget?.();
      } else {
        tawk?.showWidget?.();
      }
    };

    updateTawkVisibility();

    const interval = window.setInterval(updateTawkVisibility, 1000);

    return () => window.clearInterval(interval);
  }, [pathname]);

  // cart sync
  useEffect(() => {
    if (!isLoggedIn) {
      dispatch(clearCart());
    } else {
      dispatch(getCartListAsync());
    }
  }, [isLoggedIn, dispatch]);

  useEffect(() => {
    if (isLoggedIn && cartDraftRequest) {
      dispatch(addToCartAsync(cartDraftRequest)).finally(() => {
        dispatch(setDraftRequest(null));
      });
    }
  }, [cartDraftRequest, isLoggedIn, dispatch]);

  // GTM dataLayer login event
  useEffect(() => {
    try {
      const guestUserId = getLocalStorageData("guest-user-id");

      if (window && (window as any).dataLayer) {
        if (user) {
          const user_data: {
            phone_number?: any;
            email_address?: any;
            address?: any;
          } = { address: {} };
          user_data.phone_number = user.phone;
          user_data.email_address = user.email;
          user_data.address.first_name = user.firstName;

          (window as any).dataLayer.push({
            event: "login",
            user_id: user.id,
            user_data: user_data,
          });
        } else if (typeof guestUserId === "number") {
          (window as any).dataLayer.push({
            event: "login",
            user_id: Number(guestUserId),
            user_data: {},
          });
        } else {
          const id = randomUserId();
          setLocalStorageData("guest-user-id", id);
          (window as any).dataLayer.push({
            event: "login",
            user_id: Number(id),
            user_data: {},
          });
        }
      }
    } catch (error) {
      console.error("Error in dataLayer push", error);
    }
  }, [user]);

  // welcome popup auto-dismiss
  useEffect(() => {
    if (welcomeImage) {
      const timer = setTimeout(() => {
        setWelcomeImage(undefined);

        let expires = 1 / 24;
        if (welcomeImage.resetTime && welcomeImage.numberOfTimes) {
          expires = welcomeImage.resetTime / (60 * 24);
        }

        Cookies.set(envs.welcomePupupCloseKey, "1", { expires });
        checkClosedTime(welcomeImage.numberOfTimes || 1);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [welcomeImage]);

  // console.log("builderPageSettings", welcomePopup);

  return (
    <div
      className="flex flex-col min-h-screen"
      style={{
        ...getSectionStyle(builderPageSettings || {}),
        backgroundColor: builderPageSettings?.bgColor || "#f9f9f7",
      }}
    >
      <Header menuItems={menuItems} navItems={navItems} />
      <main className="flex-1 flex flex-col">{children}</main>
      <BackToTop />
      <Footer />

      {welcomeImage && (
        <WelcomePopup
          image={welcomeImage.desktop}
          mobile={welcomeImage.mobile}
          onClose={() => {
            setWelcomeImage(undefined);
            let expires = 1 / 24;
            if (welcomeImage.resetTime && welcomeImage.numberOfTimes) {
              expires = welcomeImage.resetTime / (60 * 24);
            }

            Cookies.set(envs.welcomePupupCloseKey, "1", { expires });
            checkClosedTime(welcomeImage.numberOfTimes || 1);
          }}
        />
      )}

      {globalPopupElement}

      <ToastContainer
        position="bottom-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default LayoutShell;
